import type { MoodKey, Strings } from '@llura/constants';

import type { CoachTone } from '../stores/coach.store';

export interface AdaptiveContext {
  /** Sessions completed in the last 7 days. */
  recentSessionCount: number;
  /** Journal entries written in the last 7 days. */
  recentJournalCount: number;
  /** Most recent mood the user tagged on a journal entry, if any. */
  lastMood: MoodKey | null;
  /** Optional override — defaults to today's date so the picked guidance is stable per day. */
  seed?: string;
}

/**
 * Chooses the daily guidance from the matching pool based on light signals
 * — no NLP on plaintext, only metadata. Intentionally simple so the user
 * can predict why a softer or firmer prompt appears.
 */
export function selectNextGuidance(
  strings: Strings,
  tone: CoachTone,
  context: AdaptiveContext,
): string {
  const sanft = strings.coach.dailyGuidance[tone].sanft;
  const fordernd = strings.coach.dailyGuidance[tone].fordernd;

  const useSanft =
    context.lastMood === 'empty' ||
    (context.recentSessionCount === 0 && context.recentJournalCount === 0);

  const pool = useSanft ? sanft : fordernd;
  const seed = context.seed ?? new Date().toISOString().slice(0, 10);
  return pickDeterministic(pool, seed + ':guidance');
}

function pickDeterministic<T>(list: readonly T[], seed: string): T {
  return list[hashString(seed) % list.length];
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
